export const COUNTRY_CODES = ["SG", "MY", "ID", "TH"] as const;
export type CountryCode = (typeof COUNTRY_CODES)[number];

export const LOCALES = ["en", "zh-CN", "ms-MY", "id-ID", "th-TH"] as const;
export type LocaleCode = (typeof LOCALES)[number];

export type Severity = "error" | "warning" | "info";
export type RiskLevel = "high" | "medium" | "low";
export type ComplianceStatus = "pass" | "review" | "fail";

export type RuleCategory =
  | "identity"
  | "tax"
  | "amount"
  | "cross_border"
  | "threshold"
  | "integrity"
  | "country_specific";

export type BuyerType = "business" | "individual" | "final_consumer" | "government";

export type BusinessActivity =
  | "general_services"
  | "professional_services"
  | "it_services"
  | "advertising_services"
  | "food_and_beverage"
  | "logistics"
  | "goods"
  | "luxury_goods"
  | "retail"
  | "other";

export type SupplyNature = "domestic" | "export_services" | "export_goods" | "exempt" | "reverse_charge";

export type SourceInputKind = "manual" | "text" | "image" | "pdf";

export type InvoiceAddress = {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  countryCode?: string;
  raw?: string;
};

export type InvoiceParty = {
  legalName?: string;
  registrationNumber?: string;
  taxNumber?: string;
  address?: InvoiceAddress;
  email?: string;
  phone?: string;
  countryCode?: string;
};

export type InvoiceItem = {
  id?: string;
  description?: string;
  classificationCode?: string;
  quantity?: number;
  unitPrice?: number;
  discountAmount?: number;
  amountExcludingTax?: number;
  taxRate?: number;
  taxType?: string;
  taxAmount?: number;
  totalAmount?: number;
};

export type InvoiceTotals = {
  subtotal?: number;
  discountTotal?: number;
  taxTotal?: number;
  grandTotal?: number;
  roundingTotal?: number;
  prepaidTotal?: number;
  currency?: string;
  exchangeRateToLocal?: number;
  localSubtotal?: number;
  localTaxTotal?: number;
  localGrandTotal?: number;
};

export type SupportingEvidence = {
  writtenContract?: boolean;
  overseasCustomerEvidence?: boolean;
  paymentProof?: boolean;
};

export type InvoiceInput = {
  country: CountryCode;
  locale?: LocaleCode;
  invoiceType?: string;
  issueDate?: string;
  issueTime?: string;
  invoiceNumber?: string;
  seller: InvoiceParty;
  buyer: InvoiceParty;
  buyerType?: BuyerType;
  items: InvoiceItem[];
  totals: InvoiceTotals;
  notes?: string[];
  taxRegistered?: boolean;
  eInvoiceRequested?: boolean;
  reverseCharge?: boolean;
  customerAccounting?: boolean;
  supplyNature?: SupplyNature;
  businessActivity?: BusinessActivity;
  turnoverLast12Months?: number;
  projectedTurnoverNext12Months?: number;
  sourceUpload?: {
    kind: SourceInputKind;
    name?: string;
    mimeType?: string;
  };
  supportingEvidence?: SupportingEvidence;
  countryExtensions?: Record<string, string | number | boolean | null | undefined>;
};

export type SourceReference = {
  id: string;
  country: CountryCode;
  authority: string;
  title: string;
  url: string;
  summary: string;
  updatedOn?: string;
  effectiveFrom?: string;
  accessedOn: string;
};

export type LocalizedText = Record<LocaleCode, string>;

export type ComplianceFinding = {
  id: string;
  ruleId: string;
  category: RuleCategory;
  severity: Severity;
  riskLevel: RiskLevel;
  title: string;
  summary: string;
  action: string;
  fieldPath?: string;
  sourceIds: string[];
};

export type ThresholdAssessment = {
  id: string;
  title: string;
  message: string;
  status: "not_applicable" | "below_threshold" | "near_threshold" | "above_threshold" | "future_liability";
  threshold: number;
  currency: string;
  currentTurnover?: number;
  projectedTurnover?: number;
  rate?: number;
  sourceIds: string[];
};

export type ComplianceSummary = {
  title: string;
  subtitle: string;
  recommendedActions: string[];
  highlightSourceIds: string[];
};

export type PreparedInvoiceItem = Required<
  Pick<InvoiceItem, "description" | "quantity" | "unitPrice" | "amountExcludingTax" | "taxRate" | "taxAmount" | "totalAmount">
> & {
  id: string;
  classificationCode?: string;
  discountAmount: number;
  taxType?: string;
};

export type PreparedInvoice = Omit<InvoiceInput, "items" | "totals" | "locale"> & {
  locale: LocaleCode;
  items: PreparedInvoiceItem[];
  totals: Required<
    Pick<
      InvoiceTotals,
      | "subtotal"
      | "discountTotal"
      | "taxTotal"
      | "grandTotal"
      | "roundingTotal"
      | "prepaidTotal"
      | "currency"
      | "exchangeRateToLocal"
    >
  > &
    Pick<InvoiceTotals, "localSubtotal" | "localTaxTotal" | "localGrandTotal">;
};

export type ComplianceEvaluation = {
  status: ComplianceStatus;
  overallRiskLevel: RiskLevel;
  country: CountryCode;
  locale: LocaleCode;
  invoiceProfile: string;
  rulebookVersion: string;
  evaluatedAt: string;
  normalizedInvoice: PreparedInvoice;
  findings: ComplianceFinding[];
  thresholds: ThresholdAssessment[];
  summary: ComplianceSummary;
  sourceIds: string[];
};

export type CountryThresholdRule = {
  id: string;
  title: LocalizedText;
  businessActivities: BusinessActivity[];
  threshold: number;
  currency: string;
  rate?: number;
  currentLogic: "past_12_months" | "calendar_year" | "projected_next_12_months" | "country_schedule";
  sourceIds: string[];
};

export type CountryProfile = {
  key: string;
  label: LocalizedText;
  requiredFields: string[];
  requiredLineFields: string[];
  allowedTaxRates: number[];
  rateNote: LocalizedText;
  sourceIds: string[];
};

export type CountrySpecificCheckResult = {
  type:
    | "required_field"
    | "format"
    | "duplicate"
    | "tax_rate"
    | "total_mismatch"
    | "cross_border"
    | "note"
    | "threshold";
  severity: Severity;
  riskLevel: RiskLevel;
  fieldPath?: string;
  sourceIds: string[];
  messageKey: string;
  params?: Record<string, string | number>;
};

export type CountryPack = {
  code: CountryCode;
  name: LocalizedText;
  localCurrency: string;
  defaultLocale: LocaleCode;
  supportedLocales: LocaleCode[];
  rulebookVersion: string;
  thresholdRules: CountryThresholdRule[];
  resolveProfile: (invoice: PreparedInvoice) => CountryProfile;
  validateRegistrationNumber?: (value: string) => boolean;
  validateTaxNumber?: (value: string) => boolean;
  extraChecks: (invoice: PreparedInvoice) => CountrySpecificCheckResult[];
  sourceIds: string[];
};
